import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  Input,
  OnDestroy,
} from "@angular/core";
import { ViewerConstants } from "../../../shared/model/viewer-constants";
import { IProjectAOI } from "../../interfaces/i-project-aoi";
import {
  GoogleAnalyticsConstantsCategory,
  GoogleAnalyticsConstantsEvents,
} from "../../../shared/model/google-analytics-constants";
import { MapService } from "../../services/internal-services/map.service";
import { FeatureFlagsService } from "../../services/internal-services/feature-flags.service";
import { FeatureFlags } from "../../model/feature-flags";
import { GoogleAnalyticsService } from "../../../shared/services/google-analytics.service";
import { VIEWER_SERVICE } from "../../services/internal-services/viewer-services/inject-base-viewer.service";
import { LoadingService } from "../../services/internal-services/loading.service";
import { Subscription } from "rxjs";

/**
 * Map Controller Component
 * Professional control panel for map interactions including zoom, mode switching (2D/3D),
 * compass navigation, infrared selector, and layer management.
 * Logic copied from collection-controller for consistency.
 */
@Component({
  selector: "app-map-controller",
  templateUrl: "./map-controller.component.html",
  styleUrl: "./map-controller.component.scss",
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MapControllerComponent implements AfterViewInit, OnDestroy {
  public viewerService = inject(VIEWER_SERVICE);

  FeatureFlags = FeatureFlags;

  private _orientation: string;

  private _mode: any;

  enableObliqueMode: boolean;

  enableObliqueByZoom: boolean;

  private _centerAOI: IProjectAOI;

  private _aois: IProjectAOI[];

  private _imageAOIs: IProjectAOI[];

  controllerRotation = 0;

  textRotation = 0;

  lockController = true;

  lockRotation = false;

  showInfraredButton3D = false;

  enableInfraredButton = false;

  is3D: boolean = false;

  lockZoomIn: boolean = false;

  lockZoomOut: boolean = false;

  // Loading state for progress bar
  showLoading = false;

  private subscriptions: Subscription[] = [];

  @Input()
  isBaseMapActivated: boolean = false;

  @Input()
  controllerBlocked = false;

  constructor(
    protected googleAnalyticsService: GoogleAnalyticsService,
    protected mapService: MapService,
    protected flagService: FeatureFlagsService,
    private loadingService: LoadingService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngAfterViewInit() {
    this.viewerService.image$.subscribe((event: any) => {
      if (event !== null) {
        const zoomInfo = this.mapService.getZoomInfo();
        this.lockZoomIn = zoomInfo.lockZoomIn;
        this.lockZoomOut = zoomInfo.lockZoomOut;
      }
    });

    this.viewerService.zoomEnd$.subscribe((event: any) => {
      if (event !== null) {
        this.lockZoomIn = event.lockZoomIn;
        this.lockZoomOut = event.lockZoomOut;
      }
    });

    // Subscribe to loading service for progress bar (using 'tiles' loadingId)
    this.subscriptions.push(
      this.loadingService.showLoading$.subscribe((loadId: string) => {
        if (loadId === "tiles") {
          this.showLoading = true;
          this.cdr.markForCheck();
        }
      }),
    );

    this.subscriptions.push(
      this.loadingService.hideLoading$.subscribe((loadId: string) => {
        if (loadId === "tiles") {
          this.showLoading = false;
          this.cdr.markForCheck();
        }
      }),
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  @Input()
  set aois(aois: IProjectAOI[]) {
    this._aois = aois;
  }

  get aois(): IProjectAOI[] {
    return this._aois;
  }

  @Input()
  set imageAOIs(aois: IProjectAOI[]) {
    this._imageAOIs = aois;
  }

  get imageAOIs(): IProjectAOI[] {
    return this._imageAOIs;
  }

  @Input()
  set centerAOI(aoi: IProjectAOI) {
    this._centerAOI = aoi;
    this.updateObliqueEnabled();
  }

  get centerAOI(): IProjectAOI {
    return this._centerAOI;
  }

  @Input()
  set mode(mode: any) {
    this._mode = mode;
    this.is3D = mode?.name === ViewerConstants.COLLECTION_MODES.OBLIQUE.name;
  }

  get mode(): any {
    return this._mode;
  }

  @Input()
  set zoom(event: any) {
    if (event !== null) {
      this.enableObliqueByZoom = event.zoom >= 19;
    }
  }

  @Input()
  set initialized(initialized: boolean) {
    this.lockController = !initialized;
    this.updateInfraredStatus();
  }

  @Input()
  set orientation(orientation: string) {
    this.updateControlOrientation(this.orientation, orientation);
    this._orientation = orientation;
    this.updateMeasureOrientation(orientation);
  }

  get orientation(): string {
    return this._orientation;
  }

  /**
   * Changes the map viewing mode between 2D (VERTICAL) and 3D (OBLIQUE)
   */
  changeMode() {
    if (!this.isEnabled()) {
      return;
    }
    this.viewerService.changeMode();
    this.updateInfraredStatus();
  }

  /**
   * Changes the map orientation to a specified cardinal direction
   * @param orientation - Target orientation (N, S, E, W, N*, I)
   */
  changeOrientation(orientation: string) {
    if (this.lockRotation || !this.orientation || !this.isEnabled() || !this.availableOrientation(orientation)) {
      return;
    }
    this.viewerService.changeOrientation(orientation);

    // eslint-disable-next-line no-param-reassign
    orientation =
      orientation == ViewerConstants.DISCRETE_ORIENTATIONS.NADIR[0]
        ? ViewerConstants.DISCRETE_ORIENTATIONS.V
        : orientation;

    this.updateInfraredStatus();
    this.googleAnalyticsService.setGoogleAnalyticsEvent(
      GoogleAnalyticsConstantsEvents.CHANGE_ORIENTATION + orientation,
      GoogleAnalyticsConstantsCategory.COLLECTION_CONTROLLER,
      true,
    );
  }

  /**
   * Checks if the orientation is North or South facing
   * @param orientation - Orientation to check
   * @returns True if orientation is N or S
   */
  checkOrientationNorthOrSouth(orientation: string): boolean {
    return (
      orientation === ViewerConstants.DISCRETE_ORIENTATIONS.N || orientation === ViewerConstants.DISCRETE_ORIENTATIONS.S
    );
  }

  /**
   * Checks if the controller is enabled and ready for interaction
   * @returns True if controller is unlocked and has valid mode and orientation
   */
  isEnabled(): boolean {
    return !!(!this.lockController && this.mode && this.orientation);
  }

  /**
   * Checks if the 3D button should be enabled
   * @returns True if all conditions for 3D mode are met
   */
  enable3DButton(): boolean {
    return (
      !this.lockController &&
      this.enableObliqueMode &&
      this.enableObliqueByZoom &&
      this.have3DOrientation() &&
      !this.isBaseMapActivated
    );
  }

  /**
   * Checks if the current AOI has oblique imagery available
   * @returns True if oblique imagery exists
   */
  haveObliqueImagery(): boolean {
    return this.centerAOI?.hasObliques() || false;
  }

  /**
   * Checks if the current orientation has 3D data available
   * @returns True if 3D orientation data exists
   */
  have3DOrientation(): boolean {
    return this.centerAOI?.hasNonArchivedOrientation(this.orientation) || false;
  }

  /**
   * Updates the oblique mode enablement based on center AOI configuration
   */
  updateObliqueEnabled() {
    let enable = false;
    if (this.centerAOI) {
      const imageAOI = this.imageAOIs.find((aoi) => aoi.getLayerId() === this.centerAOI.getLayerId());
      if (imageAOI) {
        enable = imageAOI.isEnabled();
      } else {
        enable = this.centerAOI.isEnabled3D() && this.centerAOI.isEnabled();
      }
    }
    this.enableObliqueMode = enable;
    this.updateInfraredStatus();
  }

  /**
   * Checks if a specific orientation is available for current AOI
   * @param orientation - Orientation to check
   * @returns True if orientation is available
   */
  availableOrientation(orientation: string): boolean {
    if (this.mode.name === ViewerConstants.COLLECTION_MODES.VERTICAL.name) {
      return true;
    }
    return this.centerAOI?.hasNonArchivedOrientation(orientation) || false;
  }

  /**
   * Updates the infrared button visibility and enablement status
   */
  updateInfraredStatus() {
    const cirAvailable = this.centerAOI?.isCirAvailable(this.is3D);
    this.showInfraredButton3D = !this.lockController && this.enableObliqueMode && this.is3D;
    this.enableInfraredButton =
      (this.viewerService.getZoom() >= ViewerConstants.COLLECTION_TIMELINE_ZOOM_LEVEL && cirAvailable) ||
      (this.viewerService.getZoom() < ViewerConstants.COLLECTION_TIMELINE_ZOOM_LEVEL &&
        !this.viewerService.getInfraredStatus());
  }

  /**
   * Checks if the nadir (vertical) button should be enabled in 3D mode
   * @returns True if nadir button should be enabled
   */
  enableNadirButton(): boolean {
    return !this.lockController && this.enableObliqueMode && this.is3D && this.haveNadirOrientation();
  }

  /**
   * Checks if nadir orientation is available
   * @returns True if nadir orientation exists
   */
  haveNadirOrientation(): boolean {
    return this.centerAOI?.hasNonArchivedOrientation(ViewerConstants.DISCRETE_ORIENTATIONS.NADIR[0]) || false;
  }

  /**
   * Checks if infrared orientation is available
   * @returns True if infrared orientation exists
   */
  haveInfraredOrientation(): boolean {
    return !!this.centerAOI?.orientations.some((orientation) =>
      ViewerConstants.DISCRETE_ORIENTATIONS.I.includes(orientation),
    );
  }

  /**
   * Loads the nadir (vertical) image view
   */
  loadNadirImage(): void {
    if (this.orientation !== ViewerConstants.DISCRETE_ORIENTATIONS.NADIR[0]) {
      this.changeOrientation(ViewerConstants.DISCRETE_ORIENTATIONS.NADIR[0]);
    }
  }

  /**
   * Loads the infrared (CIR) image view
   */
  loadInfraredImage(): void {
    if (this.orientation !== ViewerConstants.DISCRETE_ORIENTATIONS.I) {
      this.changeOrientation(ViewerConstants.DISCRETE_ORIENTATIONS.I);
    }
  }

  /**
   * Gets the tooltip message for 3D mode button based on current state
   * @returns Message string or null if 3D is available
   */
  get3DModeMessage(): string {
    if (this.enable3DButton() || this.viewerService.is3DMode()) {
      return null;
    }
    if (this.lockController) {
      return "Loading layers...";
    }
    if (!this.enableObliqueByZoom) {
      return `Zoom in to access 3D imagery.`;
    }
    if (this.isBaseMapActivated) {
      return "3D mode is not available when the basemap is active.";
    }
    if (!this.enableObliqueMode || !this.haveObliqueImagery()) {
      return `A 2D collection is selected on the timeline. 3D imagery is not available.`;
    }
    if (!this.have3DOrientation()) {
      return `The ${
        ViewerConstants.COLLECTION_MODES.OBLIQUE.orientations[this.orientation].description
      }-facing view is not available in 3D mode here.`;
    }
    return null;
  }

  /**
   * Handles the end of compass rotation transition
   */
  transitionRotationEnd() {
    this.lockRotation = false;
  }

  /**
   * Handles the start of compass rotation transition
   */
  transitionRotationStart() {
    this.lockRotation = true;
  }

  /**
   * Updates the compass controller rotation based on orientation change
   * @param old - Previous orientation
   * @param orientation - New orientation
   */
  updateControlOrientation(old: string, orientation: string) {
    if (!this.orientation) {
      return;
    }
    let newOrientation = orientation;
    let currentOrientation = old;
    const compassPositions = ["N", "E", "S", "W"];
    const northOrientation = compassPositions[0];

    if (
      ViewerConstants.DISCRETE_ORIENTATIONS.NADIR.includes(currentOrientation) ||
      ViewerConstants.DISCRETE_ORIENTATIONS.I === currentOrientation
    ) {
      currentOrientation = northOrientation;
    }
    if (
      ViewerConstants.DISCRETE_ORIENTATIONS.NADIR.includes(orientation) ||
      ViewerConstants.DISCRETE_ORIENTATIONS.I === orientation
    ) {
      newOrientation = northOrientation;
    }
    const currentPosition = compassPositions.indexOf(currentOrientation);
    const newPosition = compassPositions.indexOf(newOrientation);
    switch (currentPosition - newPosition) {
      case 0:
        break;
      case 1:
      case -3:
        this.controllerRotation += 90;
        this.textRotation -= 90;
        break;
      case 2:
      case -2:
        this.controllerRotation -= 180;
        this.textRotation += 180;
        break;
      case 3:
      case -1:
        this.controllerRotation -= 90;
        this.textRotation += 90;
        break;
      default:
        break;
    }
  }

  /**
   * Updates the rotation of measurement tooltips based on orientation
   * @param orientation - New orientation
   */
  updateMeasureOrientation(orientation: string) {
    const measureToolTips = Array.from(document.getElementsByClassName(ViewerConstants.MEASURE_TOOLTIPS));
    if (!this.orientation || measureToolTips.length === 0) {
      return;
    }
    const currentOrientation = {
      isNorthOrSouth: this.checkOrientationNorthOrSouth(this.orientation),
    };
    const newOrientation = {
      isNorthOrSouth: this.checkOrientationNorthOrSouth(orientation),
    };

    measureToolTips.forEach((element: any) => {
      if (currentOrientation.isNorthOrSouth !== newOrientation.isNorthOrSouth) {
        let rotation = parseFloat(element.style.transform.replace(/[^-?\d.]*/g, ""));
        if (rotation > 0) {
          rotation -= 90;
        } else {
          rotation += 90;
        }
        element.style.transform = `rotate(${rotation}deg)`;
      }
    });
  }

  /**
   * Zooms in on the map
   */
  zoomIn(): void {
    this.mapService.getCoreMap().zoomIn();
    this.googleAnalyticsService.setGoogleAnalyticsEvent(
      GoogleAnalyticsConstantsEvents.ZOOM_IN,
      GoogleAnalyticsConstantsCategory.ZOOM_CONTROLLER,
      true,
    );
  }

  /**
   * Zooms out on the map
   */
  zoomOut(): void {
    this.mapService.getCoreMap().zoomOut();
    this.googleAnalyticsService.setGoogleAnalyticsEvent(
      GoogleAnalyticsConstantsEvents.ZOOM_OUT,
      GoogleAnalyticsConstantsCategory.ZOOM_CONTROLLER,
      true,
    );
  }

  /**
   * Helper methods for template bindings
   */
  getAvailableOrientations(): Record<string, boolean> {
    return {
      N: this.availableOrientation("N"),
      E: this.availableOrientation("E"),
      S: this.availableOrientation("S"),
      W: this.availableOrientation("W"),
    };
  }

  getOrientationTooltips(): Record<string, string> {
    const tooltips: Record<string, string> = {};
    if (this.is3D) {
      ["N", "E", "S", "W"].forEach((dir) => {
        if (!this.availableOrientation(dir)) {
          const description = ViewerConstants.COLLECTION_MODES.OBLIQUE.orientations[dir]?.description || dir;
          tooltips[dir] = `The ${description}-facing view is not available in 3D mode here.`;
        }
      });
    }
    return tooltips;
  }
}
